import { Code, Table } from '@mantine/core'
import { FormattedMessage } from 'react-intl'
import { WorkspaceApiKeyScope } from '@/types/workspace'
import { ApiKeyCurlExample } from './ApiKeyCurlExample'
import { getScopeConfig } from './apiKeyUtils'

const QUERY_PARAMS = [
  {
    param: 'sort_property',
    description: 'Sort field',
    format: 'timestamp',
    example: '?sort_property=timestamp'
  },
  {
    param: 'sort_order',
    description: 'Sort direction',
    format: 'asc, desc',
    example: '?sort_order=desc'
  },
  {
    param: 'start_date',
    description: 'Events with timestamp >= start (ISO 8601)',
    format: 'ISO datetime string',
    example: '?start_date=2026-01-01T00:00:00Z'
  },
  {
    param: 'end_date',
    description: 'Events with timestamp <= end (ISO 8601)',
    format: 'ISO datetime string',
    example: '?end_date=2026-01-08T23:59:59Z'
  },
  {
    param: 'devices',
    description: 'Device ID(s) that produced the events',
    format: 'comma-separated IDs',
    example: '?devices=dev_1,dev_2'
  },
  {
    param: 'detections',
    description: 'Detection class name(s) present in events',
    format: 'comma-separated names',
    example: '?detections=Person,Car'
  },
  {
    param: 'stream_ids',
    description: 'Camera stream ID(s)',
    format: 'comma-separated IDs',
    example: '?stream_ids=cam_12,cam_21'
  },
  {
    param: 'tags',
    description: 'Event tag(s), commonly from classification pipelines',
    format: 'comma-separated tags',
    example: '?tags=Blue,Red'
  },
  {
    param: 'ocrs',
    description: 'OCR-extracted text match',
    format: 'comma-separated strings',
    example: '?ocrs=LP%20123456,ABC123'
  }
]

type PlatformEventsExampleProps = {
  keyValue: string
}

export const PlatformEventsExample = ({
  keyValue
}: PlatformEventsExampleProps) => {
  const scopeConfig = getScopeConfig(WorkspaceApiKeyScope.PlatformEvents)
  const baseUrl = window.location.origin
  const curlExample = `curl -X GET "${baseUrl}${scopeConfig.endpoint}" \\
  -H "Authorization: ApiKey ${keyValue}"`

  const urlParamsTable = (
    <Table fz="xs" mt="md" withTableBorder withColumnBorders>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Param</Table.Th>
          <Table.Th>Description</Table.Th>
          <Table.Th>Format</Table.Th>
          <Table.Th>Example</Table.Th>
        </Table.Tr>
      </Table.Thead>

      <Table.Tbody>
        {QUERY_PARAMS.map((row) => (
          <Table.Tr key={row.param}>
            <Table.Td>
              <Code fz="xs">{row.param}</Code>
            </Table.Td>
            <Table.Td>{row.description}</Table.Td>
            <Table.Td>{row.format}</Table.Td>
            <Table.Td>
              <Code fz="xs">{row.example}</Code>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )

  return (
    <ApiKeyCurlExample
      codeSnippet={curlExample}
      title={<FormattedMessage id="workspace.apiKeys.created.example" />}
      description={<FormattedMessage id={scopeConfig.descriptionId} />}
      urlParamsSlot={urlParamsTable}
      showUrlParamsToggle
    />
  )
}
